import { Common } from '@stockbit/ui';
import { Form } from 'antd';
import { useForm } from 'antd/lib/form/Form';

import { OrderButton } from 'global/TradingModal/styled';

import { useEffect } from 'react';
import { BondTransactionActionEnum } from 'global/Securities/types/api/bond';

import { formatCurrency } from 'features/securities/withdrawal/utils';
import {
  BASE_UNIT_AMOUNT,
  BOND_TRANSACTION_CONFIG,
  BondInfoType,
  BondsModalTypeEnum,
} from '../../constants';
import {
  ModalBody,
  ModalContainer,
  ModalFooter,
  ModalHeader,
  UnitInput,
} from '../../styled';
import { useBondsModalStore } from '../../stores/useBondsModalStore';
import { useBondTransaction } from '../../stores/useBondTransaction';
import useBondsAnalytics from '../../useBondsAnalytics';
import useBondPreviewForm from '../../hooks/useBondPreviewForm';
import ModalProductDisplay from '../ModalProductDisplay';
import ModalTitle from '../ModalTitle';
import Accordion from '../Accordion';
import FormDisplayField from '../FormDisplayField';
import { useBondInfoModal } from '../../stores/useBondInfoModal';

// destructures
const { Text } = Common;

export default function ModalBondSell() {
  const [form] = useForm();
  const [productId, orderId] = useBondTransaction((s) => [
    s.productId,
    s.acquisitionOrderId,
  ]);
  const { FRBondsNavigateTrackers, FRBondActionTrackers } = useBondsAnalytics();

  const [transactionType, closeTransactionModal, openConfirmModal, sourcePage] =
    useBondsModalStore((s) => [
      s.transactionType,
      s.closeTransactionModal,
      s.openConfirmModal,

      s.sourcePage,
    ]);
  const openInfoModal = useBondInfoModal((s) => s.openInfoModal);

  const {
    handleUnitAmountChanged,
    isLoadingPreview,
    unit,
    preview,
    isDisabled,
  } = useBondPreviewForm({
    action: BondTransactionActionEnum.SELL,
    productId,
    orderId,
  });

  const { textColor, title } = BOND_TRANSACTION_CONFIG[transactionType];

  const onSubmit = () => {
    FRBondActionTrackers.onOrderPlaced('Sell', productId);
    openConfirmModal();
  };

  useEffect(() => {
    if (productId) {
      FRBondsNavigateTrackers.onBondOrderPageViewed(
        'Sell',
        productId,
        sourcePage,
      );
    }
  }, []);

  return (
    <Form
      form={form}
      onFinish={onSubmit}
      colon={false}
      labelCol={{ span: 10 }}
      wrapperCol={{ span: 8, offset: 2 }}
    >
      <ModalContainer modalType={BondsModalTypeEnum.TRANSACTION}>
        <ModalHeader>
          <ModalTitle
            color={textColor}
            title={title}
            onClose={closeTransactionModal}
          />
          <ModalProductDisplay />
        </ModalHeader>
        <ModalBody>
          <FormDisplayField
            label="Unit Tersedia"
            value={preview?.sell?.available_units}
            type="unit"
            isBold
          />

          <Form.Item label="Unit Penjualan">
            <UnitInput
              value={unit}
              onChange={handleUnitAmountChanged}
              placeholder={`1 Unit = ${formatCurrency(
                BASE_UNIT_AMOUNT.toString(),
              )}`}
              type="number"
            />
          </Form.Item>

          <FormDisplayField
            label="Harga Jual (%)"
            value={preview?.sell?.price.sell}
            type="percentage"
            onInfoClick={() => openInfoModal(BondInfoType.SELLING_PRICE)}
            isNeedInfo
            isDisabled
          />
          <Accordion
            title={
              <FormDisplayField
                label="Total Nilai Penjualan"
                isLoading={isLoadingPreview}
                value={preview?.sell?.estimation_disburse_amount}
                type="currency"
                isBold
              />
            }
          >
            <FormDisplayField
              label="Nilai Penjualan"
              isLoading={isLoadingPreview}
              value={preview?.sell?.estimation_sell_amount}
              type="currency"
            />

            <FormDisplayField
              label="Kupon Berjalan (Nett)"
              isLoading={isLoadingPreview}
              value={preview?.sell?.accrued_interest_exclude_tax}
              type="currency"
              onInfoClick={() => openInfoModal(BondInfoType.ACCRUED_INTEREST)}
              isNeedInfo
              isShowSign={preview?.sell?.accrued_interest_exclude_tax > 0}
            />
            <FormDisplayField
              label="Pajak"
              isLoading={isLoadingPreview}
              value={preview?.sell?.fee.total_tax * -1}
              type="currency"
              onInfoClick={() => openInfoModal(BondInfoType.TAX)}
              isNeedInfo
              isShowSign={preview?.sell?.fee.total_tax !== 0}
            />
            <FormDisplayField
              label="Bea Meterai"
              isLoading={isLoadingPreview}
              value={preview?.sell?.fee.stamp_duty * -1}
              type="currency"
              isShowSign={preview?.sell?.fee?.stamp_duty > 0}
            />
            {preview?.sell?.seller_coupon > 0 && (
              <FormDisplayField
                label="Kupon Penjual"
                isLoading={isLoadingPreview}
                value={preview?.sell?.seller_coupon}
                type="currency"
                onInfoClick={() =>
                  openInfoModal(BondInfoType.ACCRUED_INTEREST_SELLER)
                }
                isNeedInfo
                isShowSign={preview?.sell?.seller_coupon > 0}
              />
            )}
          </Accordion>

          <Accordion
            title={
              <FormDisplayField
                label="P&L (Nett)"
                isLoading={isLoadingPreview}
                value={preview?.sell?.profit_loss_nett}
                type={preview?.sell?.profit_loss_nett === 0 ? 'currency' : 'pl'}
                plPercentage={preview?.sell?.profit_loss_nett_percentage}
                isBold
              />
            }
          >
            <FormDisplayField
              label="Kupon Berjalan"
              isLoading={isLoadingPreview}
              value={preview?.sell?.accrued_interest_exclude_tax}
              type={
                preview?.sell?.accrued_interest_exclude_tax === 0
                  ? 'currency'
                  : 'pl'
              }
              onInfoClick={() => openInfoModal(BondInfoType.TOTAL_DAILY_COUPON)}
              isNeedInfo
            />
            <FormDisplayField
              label="Capital Gain / Loss"
              isLoading={isLoadingPreview}
              value={preview?.sell?.profit_loss}
              type={preview?.sell?.profit_loss === 0 ? 'currency' : 'pl'}
            />
            <FormDisplayField
              label="Pajak"
              isLoading={isLoadingPreview}
              value={preview?.sell?.fee.total_tax * -1}
              type="currency"
              onInfoClick={() => openInfoModal(BondInfoType.TAX)}
              isNeedInfo
              isShowSign={preview?.sell?.fee.total_tax !== 0}
            />
          </Accordion>

          <Accordion
            title={
              <FormDisplayField
                label="Total Keuntungan (Nett)"
                subtitle="Selama Investasi"
                isLoading={isLoadingPreview}
                value={preview?.sell?.total_realized_amount}
                type={
                  preview?.sell?.total_realized_amount === 0 ? 'currency' : 'pl'
                }
                plPercentage={preview?.sell?.total_realized_amount_percentage}
                isBold
              />
            }
          >
            <FormDisplayField
              label="Total Kupon Harian"
              isLoading={isLoadingPreview}
              value={preview?.sell?.daily_accrued_interest}
              type={
                preview?.sell?.daily_accrued_interest === 0 ? 'currency' : 'pl'
              }
              onInfoClick={() => openInfoModal(BondInfoType.TOTAL_DAILY_COUPON)}
              isNeedInfo
            />
            <FormDisplayField
              label="Pajak"
              isLoading={isLoadingPreview}
              value={preview?.sell?.fee.total_tax * -1}
              type="currency"
              onInfoClick={() => openInfoModal(BondInfoType.TAX)}
              isNeedInfo
              isShowSign={preview?.sell?.fee.total_tax !== 0}
            />
            <FormDisplayField
              label="Capital Gain / Loss"
              isLoading={isLoadingPreview}
              value={preview?.sell?.profit_loss}
              type={preview?.sell?.profit_loss === 0 ? 'currency' : 'pl'}
            />
          </Accordion>
        </ModalBody>
      </ModalContainer>
      <ModalFooter>
        <OrderButton
          disabled={isDisabled}
          type="secondary"
          htmlType="submit"
          onClick={openConfirmModal}
          block
        >
          <Text>Place Order</Text>
        </OrderButton>
      </ModalFooter>
    </Form>
  );
}
